import React, { useState, useEffect, useMemo } from 'react'
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { supabase, isAdmin, isBingoAdmin, setAdminSession, isNationalsAdmin } from './lib/supabase'
import { useAnalytics } from './lib/useAnalytics'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import { useMemberSession } from './components/MemberAuthGate'

import PublicLeaderboard from './pages/PublicLeaderboard'
import AdminLogin from './pages/AdminLogin'
import AdminDashboard from './pages/AdminDashboard'
import TeamManagement from './pages/TeamManagement'
import WeighmasterInterface from './pages/WeighmasterInterface'
import CheckInDisplay from './pages/CheckInDisplay'
import ResultsManagement from './pages/ResultsManagement'
import RecordApplication from './pages/RecordApplication'
import CompetitionsPage from './pages/CompetitionsPage'
import MembershipRouter from './pages/MembershipPage'
import MembershipAdmin from './pages/MembershipAdmin'
import CompetitionDetail from './pages/CompetitionDetail'
import CompRegister from './pages/CompRegister'
import CompAdmin from './pages/CompAdmin'
import NationalsPage from './pages/NationalsPage'
import MudgewayPage from './pages/MudgewayPage'
import MudgewayChallenge from './pages/MudgewayChallenge'
import MudgewayRespond from './pages/MudgewayRespond'
import MudgewayAdmin from './pages/MudgewayAdmin'
import NationalsAdmin from './pages/NationalsAdmin'
import NationalsRegister from './pages/NationalsRegister'
import NationalsRegisterIndividual from './pages/NationalsRegisterIndividual'
import NationalsConfirm from './pages/NationalsConfirm'
import NationalsBuddyFinder from './pages/NationalsBuddyFinder'
import NationalsLeaderboard from './pages/NationalsLeaderboard'
import CatfishCullPage from './pages/CatfishCullPage'
import CatfishCullRegister from './pages/CatfishCullRegister'
import CatfishConfirm from './pages/CatfishConfirm'
import CompSuperAdmin from './pages/CompSuperAdmin'
import CompDeepLink from './pages/CompDeepLink'
import ApplicationArchive from './pages/ApplicationArchive'
import RecordsAdmin from './pages/RecordsAdmin'
import FishIDPage from './pages/FishIDPage'
import FishSpeciesAdmin from './pages/FishSpeciesAdmin'
import AnalyticsDashboard from './pages/AnalyticsDashboard'
import BingoAdmin from './pages/BingoAdmin'
import BingoSpeciesAdmin from './pages/BingoSpeciesAdmin'
import BingoBonusAdmin from './pages/BingoBonusAdmin'
import BingoConfigAdmin from './pages/BingoConfigAdmin'
import BingoPhotoExportAdmin from './pages/BingoPhotoExportAdmin'
import BingoRegistrationsAdmin from './pages/BingoRegistrationsAdmin'
import InspirationPage from './pages/InspirationPage'
import CarouselAdmin from './pages/CarouselAdmin'
import HubCarousel from './components/HubCarousel'
import BigFishPage from './pages/BigFishPage'
import BigFishAdmin from './pages/BigFishAdmin'
import BigFishSponsorPage from './pages/BigFishSponsorPage'
import BingoApp from './pages/bingo/BingoApp'
import BingoDiverPage from './pages/bingo/BingoDiverPage'
import WFSCApp from './wfsc/WFSCApp'
import AGMPage from './pages/AGMPage'
import AGMAdmin from './pages/AGMAdmin'
import DevSquadPage from './pages/DevSquadPage'
import DevSquadAdmin from './pages/DevSquadAdmin'
import NearMissReport from './pages/NearMissReport'
import NearMissThanks from './pages/NearMissThanks'
import NearMissResults from './pages/NearMissResults'
import NearMissAdmin from './pages/NearMissAdmin'

function ProtectedRoute({ children }) {
  const location = useLocation()
  if (!isAdmin()) return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />
  return children
}

// Fish Bingo admin accepts the scoped bingo password in addition to full
// sys admin — used only for /bingo/admin/* so a bingo-only login can't
// reach any other admin area.
function ProtectedBingoRoute({ children }) {
  const location = useLocation()
  if (!isBingoAdmin()) return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />
  return children
}

// Nationals admin accepts the scoped nationals password in addition to full
// sys admin — used only for /nationals/admin so a nationals-only login can't
// reach any other admin area.
function ProtectedNationalsRoute({ children }) {
  const location = useLocation()
  if (!isNationalsAdmin()) return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />
  return children
}

const RECORD_DIVISIONS = ['All', 'Open', "Women's", 'Junior U18']
const SNZ_BLUE = '#2B6CB0'
const SNZ_LOGO = import.meta.env.VITE_SNZ_LOGO_URL || null

// ── Member badge — shown in blue nav bars across all pages ────────────────────
function MemberBadge() {
  const { member, session } = useMemberSession()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  if (!session) return (
    <button onClick={() => navigate('/membership/login')}
      className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-semibold transition px-2 py-1 rounded-lg hover:bg-white/10">
      Sign in
    </button>
  )
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition">
        <div className="w-6 h-6 rounded-full bg-white/30 flex items-center justify-center text-white font-black text-xs flex-shrink-0">
          {member?.name?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="text-left hidden sm:block">
          <div className="text-white font-bold text-xs leading-tight">{member?.name || 'Member'}</div>
          <div className="text-blue-200 text-xs leading-tight">{member?.member_number || 'SNZ Member'}</div>
        </div>
        <span className="text-white/50 text-xs ml-1">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <button onClick={() => { setOpen(false); navigate('/') }}
            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            ⌂ SNZ Hub
          </button>
          <button onClick={() => { setOpen(false); navigate('/membership/dashboard') }}
            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            My Membership
          </button>
          <div className="border-t border-gray-100" />
          <button onClick={async () => { setOpen(false); await supabase.auth.signOut(); navigate('/') }}
            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50">
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

// White-background version for the Hub landing header
function HubMemberBadge() {
  const { member, session } = useMemberSession()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  if (!session) return (
    <button onClick={() => navigate('/membership/login')}
      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">
      Sign in
    </button>
  )
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0"
          style={{ background: '#2B6CB0' }}>
          {member?.name?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="text-left hidden sm:block">
          <div className="text-gray-900 font-bold text-xs leading-tight">{member?.name || 'Member'}</div>
          <div className="text-gray-400 text-xs leading-tight">{member?.member_number || 'SNZ Member'}</div>
        </div>
        <span className="text-gray-400 text-xs ml-1">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <button onClick={() => { setOpen(false); navigate('/membership/dashboard') }}
            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            My Membership
          </button>
          <div className="border-t border-gray-100" />
          <button onClick={async () => { setOpen(false); await supabase.auth.signOut(); navigate('/') }}
            className="w-full text-left px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50">
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

const divBadgeClass = d =>
  d === 'Open'        ? 'bg-blue-50 text-blue-700 border border-blue-200' :
  d === "Women's"     ? 'bg-pink-50 text-pink-700 border border-pink-200' :
  d === 'Meritorious' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                        'bg-purple-50 text-purple-700 border border-purple-200'


// ── Shared helpers ───────────────────────────────────────────────────────────
const SUPABASE_STORAGE = import.meta.env.VITE_SUPABASE_URL
  ? `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/snz-media`
  : null

function PhotoModal({ url, caption, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.92)' }}
      onClick={onClose}
    >
      <div className="relative max-w-4xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white text-lg font-bold flex items-center justify-center transition"
        >✕</button>
        <img
          src={url}
          alt={caption || 'Record photo'}
          className="rounded-xl shadow-2xl object-contain max-h-[82vh] max-w-full"
        />
        {caption && (
          <p className="text-white/70 text-sm text-center mt-4 px-4">{caption}</p>
        )}
        <p className="text-white/30 text-xs mt-2">Press Esc or click outside to close</p>
      </div>
    </div>
  )
}

if (typeof document !== 'undefined' && !document.getElementById('deep-link-highlight-style')) {
  const style = document.createElement('style')
  style.id = 'deep-link-highlight-style'
  style.textContent = `
    @keyframes deepLinkFlash {
      0% { background-color: rgba(251, 191, 36, 0); box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
      20% { background-color: rgba(251, 191, 36, 0.4); box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.6); }
      100% { background-color: rgba(251, 191, 36, 0); box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
    }
    .deep-link-highlight {
      animation: deepLinkFlash 2.5s ease-out;
      position: relative;
      z-index: 1;
    }
  `
  document.head.appendChild(style)
}

function ShareButton({ id, prefix = 'record', small = false }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async (e) => {
    e.stopPropagation()
    e.preventDefault()
    const url = `${window.location.origin}/records#${prefix}-${id}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      window.prompt('Copy this link:', url)
    }
  }
  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy share link'}
      className={`inline-flex items-center gap-1 rounded-md border transition text-xs font-bold ${
        copied
          ? 'border-green-300 bg-green-50 text-green-700'
          : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300 hover:text-blue-600'
      } ${small ? 'px-1.5 py-1' : 'px-2 py-1'}`}
      type="button"
    >
      {copied ? '✓' : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
      )}
      {!small && <span>{copied ? 'Copied' : 'Share'}</span>}
    </button>
  )
}

function useDeepLinkHighlight(records, prefix, onBeforeHighlight) {
  const [triggered, setTriggered] = useState(false)

  useEffect(() => {
    if (!records || records.length === 0) return
    const hash = window.location.hash
    if (!hash || !hash.startsWith(`#${prefix}-`)) return
    const id = hash.replace(`#${prefix}-`, '')
    const match = records.find(r => String(r.id) === String(id))
    if (!match) return
    if (onBeforeHighlight) onBeforeHighlight(match)
    setTriggered(true)
  }, [records, prefix])

  useEffect(() => {
    if (!triggered) return
    const hash = window.location.hash
    const id = hash.replace(`#${prefix}-`, '')
    let attempts = 0
    const tryHighlight = () => {
      const els = document.querySelectorAll(`[data-deeplink="${prefix}-${id}"]`)
      let el = null
      for (const candidate of els) {
        const r = candidate.getBoundingClientRect()
        if (r.width > 0 && r.height > 0) { el = candidate; break }
      }
      if (el) {
        const rect = el.getBoundingClientRect()
        const targetY = rect.top + window.pageYOffset - (window.innerHeight / 2) + (rect.height / 2)
        window.scrollTo(0, Math.max(0, targetY))
        setTimeout(() => { window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' }) }, 50)
        el.classList.add('deep-link-highlight')
        setTimeout(() => el.classList.remove('deep-link-highlight'), 2600)
        return
      }
      attempts++
      if (attempts < 20) setTimeout(tryHighlight, 200)
    }
    setTimeout(tryHighlight, 300)
  }, [triggered, prefix])
}

// ── NZ Records page (enhanced) ───────────────────────────────────────────────
function NZRecordsPage() {
  const navigate = useNavigate()
  const { session } = useMemberSession()
  const [filter, setFilter] = useState('All')
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('species') // 'species' | 'weight'
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lightbox, setLightbox] = useState(null)

  useDeepLinkHighlight(records, 'record', (match) => {
    setFilter('All')
    setSearch('')
    if (match.division === 'Meritorious') {
      window.location.hash = `meritorious-${match.id}`
    }
  })

  useEffect(() => {
    supabase
      .from('nz_records')
      .select('*')
      .order('species', { ascending: true })
      .then(({ data, error }) => {
        if (error) { setError('Failed to load records.'); console.error(error) }
        else setRecords(data || [])
        setLoading(false)
      })
  }, [])

  const filtered = records
    .filter(r =>
      r.division !== 'Meritorious' &&
      (filter === 'All' || r.division === filter) &&
      (search === '' ||
        r.species.toLowerCase().includes(search.toLowerCase()) ||
        (r.diver && r.diver.toLowerCase().includes(search.toLowerCase())))
    )
    .sort((a, b) => {
      if (sortBy === 'weight') return (b.weight_kg || 0) - (a.weight_kg || 0)
      return a.species.localeCompare(b.species)
    })

  const fmtWeight = (w) => w != null ? `${w} kg` : '—'

  return (
    <div className="min-h-screen bg-white">
      {lightbox && <PhotoModal url={lightbox.url} caption={lightbox.caption} onClose={() => setLightbox(null)} />}

      <div style={{ background: SNZ_BLUE }} className="px-6 py-3 flex items-center justify-between border-b border-blue-700">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-white font-bold text-sm bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition">← SNZ Hub</button>
          <span className="text-blue-200 text-sm opacity-75">/ NZ Records</span>
        </div>
        <div className="flex items-center gap-2">
          <MemberBadge />
          <button onClick={() => navigate('/records/admin')}
            className="flex items-center gap-1.5 text-white font-bold text-sm bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition">⚙ Admin</button>
        </div>
      </div>

      <header className="border-b border-gray-200 px-6 py-4 bg-white flex items-center gap-4">
        {SNZ_LOGO && <img src={SNZ_LOGO} alt="Spearfishing NZ" className="h-10 w-auto object-contain" />}
        <div>
          <h1 className="text-2xl font-black text-gray-900">NZ Spearfishing Records</h1>
          <p className="text-xs text-gray-400 tracking-wider">Official records ratified by Spearfishing New Zealand</p>
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap justify-end">
          <button
            onClick={() => session ? navigate('/apply') : navigate('/membership/login')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition hover:opacity-90"
            style={{ background: '#16a34a' }}
          >+ Submit a Record Claim</button>
          <button
            onClick={() => navigate('/awards')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition"
          >🏆 Annual Awards</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6">
        {/* Filters */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Search species or diver..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-white border border-gray-300 text-gray-900 placeholder-gray-400 rounded-lg px-4 py-2 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <div className="flex gap-2 flex-wrap">
            {RECORD_DIVISIONS.map(d => (
              <button key={d} onClick={() => setFilter(d)}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold transition border"
                style={filter === d
                  ? { background: SNZ_BLUE, color: '#fff', borderColor: SNZ_BLUE }
                  : { background: '#fff', color: '#374151', borderColor: '#d1d5db' }}
              >{d}</button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="bg-white border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
          >
            <option value="species">Sort: A–Z</option>
            <option value="weight">Sort: Heaviest</option>
          </select>
          <span className="text-xs text-gray-400">{loading ? '…' : `${filtered.length} records`}</span>
        </div>

        {loading && <div className="text-center py-16 text-gray-400">Loading records…</div>}
        {error && <div className="text-center py-16 text-red-500">{error}</div>}

        {!loading && !error && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px]">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-3 py-3 w-12"></th>
                    {['Species','Record','Division','Diver','Club','Location','Date',''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold tracking-widest text-gray-400 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={r.id} data-deeplink={`record-${r.id}`} className={`border-b border-gray-100 hover:bg-blue-50 transition ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="px-3 py-2">
                        {r.photo_url ? (
                          <button
                            onClick={() => setLightbox({ url: r.photo_url, caption: `${r.species} — ${r.diver} · ${r.weight_kg} kg · ${r.location}` })}
                            className="relative group w-12 h-12 flex-shrink-0 block"
                            title="View photo"
                          >
                            <img
                              src={r.photo_url}
                              alt={r.species}
                              className="w-12 h-12 object-cover rounded-lg border border-gray-200 group-hover:border-blue-400 transition group-hover:brightness-75"
                            />
                            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-white text-base">
                              🔍
                            </span>
                          </button>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-200 text-xl">🐟</div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-bold text-gray-900">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.species}
                          {r.provisional && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap">
                              Provisional
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xl font-black" style={{ color: SNZ_BLUE }}>{fmtWeight(r.weight_kg)}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-bold ${divBadgeClass(r.division)}`}>{r.division}</span></td>
                      <td className="px-4 py-3 text-gray-700">{r.diver}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{r.club}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{r.location}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{r.date_caught}</td>
                      <td className="px-3 py-3"><ShareButton id={r.id} prefix="record" small /></td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">No records match your search</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2 mb-6">
              {filtered.map((r) => (
                <div key={r.id} data-deeplink={`record-${r.id}`} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex items-center gap-3 p-3">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0">
                    {r.photo_url ? (
                      <button
                        onClick={() => setLightbox({ url: r.photo_url, caption: `${r.species} — ${r.diver} · ${r.weight_kg} kg · ${r.location}` })}
                        className="relative group block w-16 h-16"
                      >
                        <img
                          src={r.photo_url}
                          alt={r.species}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200 group-active:brightness-75 transition"
                        />
                        <span className="absolute inset-0 flex items-center justify-center rounded-lg opacity-0 group-active:opacity-100 bg-black/30 transition text-white text-xl">🔍</span>
                      </button>
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-2xl text-gray-200">🐟</div>
                    )}
                  </div>
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="font-bold text-gray-900 text-sm leading-tight truncate">{r.species}</span>
                        {r.provisional && (
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap flex-shrink-0">Provisional</span>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${divBadgeClass(r.division)}`}>{r.division}</span>
                    </div>
                    <div className="text-lg font-black leading-tight" style={{ color: SNZ_BLUE }}>{fmtWeight(r.weight_kg)}</div>
                    <div className="text-xs font-semibold text-gray-700 truncate">{r.diver}</div>
                    <div className="text-xs text-gray-400 truncate">{r.location} · {r.date_caught}</div>
                  </div>
                  <div className="flex-shrink-0 self-start"><ShareButton id={r.id} prefix="record" small /></div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Submit */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            Do you have a NEW SPEARFISHING RECORD? Submit your claim online, or download the PDF form and email it in.
          </p>
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={() => session ? navigate('/apply') : navigate('/membership/login')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm text-white transition hover:opacity-90"
              style={{ background: SNZ_BLUE }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Apply Online
            </button>
            <a
              href="https://zodqgekuackcrqyzluoo.supabase.co/storage/v1/object/public/snz-media/awards/nz%20spearfishing%20record%20application%20form%20v2018%20(1).pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm border-2 border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
              Download PDF Form
            </a>
          </div>
          <p className="text-xs text-gray-400">
            Email completed PDF forms and photos to{' '}
            <a href="mailto:records@spearfishingnz.co.nz" className="underline font-semibold" style={{ color: SNZ_BLUE }}>records@spearfishingnz.co.nz</a>
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Annual Awards page ───────────────────────────────────────────────────────
function AwardsPage() {
  const navigate = useNavigate()
  const [awards, setAwards] = useState([])
  const [meritorious, setMeritorious] = useState([])
  const [year, setYear] = useState(new Date().getFullYear())
  const [years, setYears] = useState([])
  const [loading, setLoading] = useState(true)
  const [merLoading, setMerLoading] = useState(true)
  const [lightbox, setLightbox] = useState(null)
  const [merSearch, setMerSearch] = useState('')
  const [merLightbox, setMerLightbox] = useState(null)

  useEffect(() => {
    supabase.from('snz_awards').select('year').order('year', { ascending: false })
      .then(({ data }) => {
        if (data) {
          const unique = [...new Set(data.map(r => r.year))]
          setYears(unique)
          if (unique.length > 0) setYear(unique[0])
        }
      })
    // Fetch all meritorious records
    supabase.from('nz_records').select('*').eq('division', 'Meritorious').order('date_caught', { ascending: false })
      .then(({ data }) => { setMeritorious(data || []); setMerLoading(false) })
  }, [])

  useEffect(() => {
    if (!year) return
    setLoading(true)
    supabase.from('snz_awards').select('*').eq('year', year).order('category')
      .then(({ data }) => { setAwards(data || []); setLoading(false) })
  }, [year])

  const categoryIcon = (cat) => {
    if (cat.toLowerCase().includes('meritorious')) return '⭐'
    if (cat.toLowerCase().includes('snapper')) return '🐠'
    if (cat.toLowerCase().includes('kingfish')) return '🐟'
    return '🏆'
  }

  const filteredMer = meritorious.filter(r =>
    merSearch === '' ||
    r.species.toLowerCase().includes(merSearch.toLowerCase()) ||
    (r.diver || '').toLowerCase().includes(merSearch.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-white">
      {lightbox && <PhotoModal url={lightbox.url} caption={lightbox.caption} onClose={() => setLightbox(null)} />}

      <div style={{ background: SNZ_BLUE }} className="px-6 py-3 flex items-center justify-between border-b border-blue-700">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/records')} className="flex items-center gap-1.5 text-white font-bold text-sm bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition">← NZ Records</button>
          <span className="text-blue-200 text-sm opacity-75">/ Annual Awards</span>
        </div>
        <div className="flex items-center gap-2">
          <MemberBadge />
          <button onClick={() => navigate('/records/admin')}
            className="flex items-center gap-1.5 text-white font-bold text-sm bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition">⚙ Admin</button>
        </div>
      </div>

      <header className="border-b border-gray-200 px-6 py-6 bg-white">
        <div className="max-w-3xl mx-auto flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-black text-gray-900 mb-1">Annual Awards</h1>
            <p className="text-gray-400 text-sm">Spearfishing New Zealand · Year in Review</p>
          </div>

        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6">

        {/* ── Meritorious Fish ── */}
        <div className="mt-10">
          <div className="mb-4">
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">⭐ Meritorious Fish</h2>
            <p className="text-sm text-gray-400 mt-1">Exceptional or unusual catches recognised by Spearfishing New Zealand</p>
          </div>

          {/* Search */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4 flex gap-3 items-center">
            <input
              type="text"
              placeholder="Search species or diver..."
              value={merSearch}
              onChange={e => setMerSearch(e.target.value)}
              className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <span className="text-xs text-gray-400">{merLoading ? '…' : `${filteredMer.length} entries`}</span>
          </div>

          {merLoading && <div className="text-center py-8 text-gray-400">Loading…</div>}

          {!merLoading && filteredMer.length === 0 && (
            <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl">No meritorious fish recorded yet.</div>
          )}

          {!merLoading && filteredMer.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden mb-4 shadow-sm">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-amber-50">
                      <th className="px-3 py-3 w-12"></th>
                      {['Species','Weight','Diver','Club','Location','Date'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold tracking-widest text-amber-700 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMer.map((r, i) => (
                      <tr key={r.id} className={`border-b border-gray-100 hover:bg-amber-50 transition ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                        <td className="px-3 py-2">
                          {r.photo_url ? (
                            <button onClick={() => setMerLightbox({ url: r.photo_url, caption: `${r.species} — ${r.diver}${r.weight_kg ? ` · ${r.weight_kg} kg` : ''} · ${r.location}` })}
                              className="relative group block w-12 h-12">
                              <img src={r.photo_url} alt={r.species} className="w-12 h-12 object-cover rounded-lg border-2 border-amber-300 group-hover:brightness-75 transition"/>
                              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-white text-base">🔍</span>
                            </button>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center text-xl">⭐</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-bold text-gray-900">
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.species}
                          {r.provisional && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 whitespace-nowrap">
                              Provisional
                            </span>
                          )}
                        </div>
                      </td>
                        <td className="px-4 py-3 font-bold text-amber-600">{r.weight_kg ? `${r.weight_kg} kg` : '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{r.diver}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{r.club}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{r.location}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{r.date_caught}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-2 mb-4">
                {filteredMer.map(r => (
                  <div key={r.id} className="bg-white border border-amber-200 rounded-xl overflow-hidden shadow-sm flex items-center gap-3 p-3">
                    <div className="flex-shrink-0">
                      {r.photo_url ? (
                        <button onClick={() => setMerLightbox({ url: r.photo_url, caption: `${r.species} — ${r.diver}` })}
                          className="relative group block w-16 h-16">
                          <img src={r.photo_url} alt={r.species} className="w-16 h-16 object-cover rounded-lg border-2 border-amber-300 group-active:brightness-75 transition"/>
                        </button>
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center text-2xl">⭐</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm truncate">{r.species}</p>
                      {r.weight_kg && <p className="font-black text-amber-600">{r.weight_kg} kg</p>}
                      <p className="text-xs font-semibold text-gray-700 truncate">{r.diver}</p>
                      <p className="text-xs text-gray-400 truncate">{r.location} · {r.date_caught}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Meritorious lightbox */}
          {merLightbox && <PhotoModal url={merLightbox.url} caption={merLightbox.caption} onClose={() => setMerLightbox(null)} />}
        </div>
      </div>
    </div>
  )
}

// ── News page ────────────────────────────────────────────────────────────────
function NewsPage() {
  const navigate = useNavigate()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('snz_news').select('*').eq('published', true)
      .order('published_at', { ascending: false })
      .then(({ data }) => { setPosts(data || []); setLoading(false) })
  }, [])

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })

  const categoryBadge = (cat) => {
    if (cat === 'record') return { label: 'New Record', cls: 'bg-blue-50 text-blue-700 border border-blue-200' }
    if (cat === 'event') return { label: 'Event', cls: 'bg-orange-50 text-orange-700 border border-orange-200' }
    return { label: 'News', cls: 'bg-green-50 text-green-700 border border-green-200' }
  }

  return (
    <div className="min-h-screen bg-white">
      <div style={{ background: SNZ_BLUE }} className="px-6 py-3 flex items-center justify-between border-b border-blue-700">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-white font-bold text-sm bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition">← SNZ Hub</button>
          <span className="text-blue-200 text-sm opacity-75">/ News & Updates</span>
        </div>
        <MemberBadge />
      </div>
      <header className="border-b border-gray-200 px-6 py-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-black text-gray-900">News & Updates</h1>
          <p className="text-gray-400 text-sm mt-1">Latest from Spearfishing New Zealand</p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6">
        {loading && <div className="text-center py-16 text-gray-400">Loading…</div>}
        {!loading && posts.length === 0 && (
          <div className="text-center py-16 text-gray-400">No posts yet.</div>
        )}
        {!loading && posts.map(p => {
          const badge = categoryBadge(p.category)
          return (
            <article key={p.id} className="mb-6 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              {p.photo_url && <img src={p.photo_url} alt={p.title} className="w-full h-52 object-cover" />}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                  <span className="text-xs text-gray-400">{fmtDate(p.published_at)}</span>
                  {p.author && <span className="text-xs text-gray-400">· {p.author}</span>}
                </div>
                <h2 className="text-xl font-black text-gray-900 mb-3">{p.title}</h2>
                <p className="text-gray-600 leading-relaxed text-sm whitespace-pre-line">{p.body}</p>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}


const STATUS = {
  live:    { label: 'Live',             bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
  soon:    { label: 'Coming Soon',      bg: '#fffbeb', color: '#b45309', border: '#fcd34d' },
  wrapped: { label: 'Season Wrapped',   bg: '#f8fafc', color: '#64748b', border: '#cbd5e1' },
  dev:     { label: 'In Development',   bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  concept: { label: 'New',              bg: '#faf5ff', color: '#7c3aed', border: '#ddd6fe' },
  sponsor: { label: 'Live',                  bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
}

function SNZHub() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({})
  const [bigFishCount, setBigFishCount] = useState(null)
  const [mudgewayHolder, setMudgewayHolder] = useState(null)
  const [agmMeetings, setAgmMeetings] = useState([])

  useEffect(() => {
    Promise.all([
      supabase.from('members').select('id', { count: 'exact', head: true }).eq('membership_status', 'active'),
      supabase.from('nz_records').select('id', { count: 'exact', head: true }).neq('division', 'Meritorious'),
      supabase.from('nz_records').select('id', { count: 'exact', head: true }).eq('provisional', true),
      supabase.from('competitions').select('id', { count: 'exact', head: true }),
      supabase.from('competitions').select('id', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('snz_recipes').select('id', { count: 'exact', head: true }).eq('is_approved', true),
    ]).then(([m, r, rp, ct, ca, rec]) => setStats({
      members:      m.count  ?? 0,
      records:      r.count  ?? 0,
      provisional:  rp.count ?? 0,
      compsTotal:   ct.count ?? 0,
      compsActive:  ca.count ?? 0,
      recipes:      rec.count ?? 0,
    }))
  }, [])

  // Current Mudgeway holder for the hub tile — read live rather than hardcoded,
  // since the whole point of the module is that custody changes hands.
  useEffect(() => {
    supabase.from('mudgeway_holder_history')
      .select('held_from, clubs(name)')
      .is('held_until', null)
      .maybeSingle()
      .then(({ data }) => setMudgewayHolder(data || null))
  }, [])

  useEffect(() => {
    supabase.from('agm_meetings').select('id, title, kind, status, meeting_date')
      .in('status', ['published', 'open'])
      .order('meeting_date', { ascending: true })
      .then(({ data }) => setAgmMeetings(data || []))
  }, [])

  // Fish Bingo season config for the hub tile — read live rather than
  // hardcoded, so the badge/summary flip from "register now" to "live" to
  // "wrapped" on their own as the season progresses. No deploy needed.
  const [bingoCfg, setBingoCfg] = useState(null)
  useEffect(() => {
    supabase.from('bingo_comp_config').select('season, status, comp_start, comp_end')
      .eq('is_active', true).maybeSingle()
      .then(({ data }) => setBingoCfg(data || null))
  }, [])
  // Dev Squad visibility switch (migration 038) — 'hidden' removes the tile.
  const [devSquadCfg, setDevSquadCfg] = useState(null)
  useEffect(() => {
    supabase.from('dev_squad_config').select('status').eq('id', 1).maybeSingle()
      .then(({ data }) => setDevSquadCfg(data || { status: 'hidden' }))
  }, [])

  const bingoWindow = useMemo(() => {
    if (!bingoCfg) return null
    if (bingoCfg.status) return bingoCfg.status === 'active' ? 'open' : bingoCfg.status === 'upcoming' ? 'before' : 'after'
    const now = new Date()
    if (now < new Date(bingoCfg.comp_start)) return 'before'
    if (now > new Date(bingoCfg.comp_end)) return 'after'
    return 'open'
  }, [bingoCfg])

  useEffect(() => {
    supabase.from('bigfish_comps').select('id').eq('is_active', true).maybeSingle()
      .then(async ({ data: comp }) => {
        if (!comp) return
        const [{ data: regs }, { data: ents }] = await Promise.all([
          supabase.from('bigfish_registrations').select('user_id').eq('comp_id', comp.id),
          supabase.from('bigfish_entries').select('user_id').eq('comp_id', comp.id),
        ])
        const ids = new Set([...(regs || []).map(r => r.user_id), ...(ents || []).map(e => e.user_id)])
        setBigFishCount(ids.size)
      })
  }, [])

  const modules = [
    {
      title: 'Membership',
      desc: 'Join SNZ — a small annual fee supports your national body. Manage your profile, track your competition history, and access member benefits.',
      onClick: () => navigate('/membership'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
      status: 'live',
      summary: stats.members ? `${stats.members} active members` : null,
    },
    {
      title: 'NZ Records',
      desc: 'Official national spearfishing records by species, division, and category. Submit and verify new claims.',
      onClick: () => navigate('/records'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
      status: 'live',
      summary: stats.records
        ? `${stats.records} ratified records${stats.provisional ? ` · ${stats.provisional} provisional` : ''}`
        : null,
    },
    {
      title: 'Catfish Cull',
      desc: 'Rosemergy Catfish Cull · Motuoapa, Lake Taupō · 13 Feb 2027. Registration, 2026 results, event info and admin.',
      onClick: () => navigate('/catfish'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
      status: 'live',
      summary: 'Registration open for 2027',
    },
    {
      title: 'National Championships',
      desc: 'Tairua, Coromandel · 19–24 Jan 2027 · 8 events including Open, Juniors, Women\'s, Fin Swim, Photography, Silver & Golden Oldie, and Super Diver.',
      onClick: () => navigate('/nationals'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/></svg>,
      status: 'live',
      summary: 'Registrations open now',
    },
    {
      title: 'Diver Development Squad',
      desc: 'Identifying and developing the divers who will represent New Zealand on the world stage. Six months of focused training, a boot camp into the 2027 Nationals, and a pathway to the World Champs. Applications are open — you don\'t need to be a current top diver to apply.',
      onClick: () => navigate('/dev-squad'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v6"/><path d="m15.5 4.5-3.5 3.5-3.5-3.5"/><circle cx="12" cy="14" r="4"/><path d="M12 18v4"/><path d="M8 22h8"/></svg>,
      status: devSquadCfg?.status === 'closed' ? 'soon' : 'live',
      statusLabel: devSquadCfg?.status === 'closed' ? 'Applications Closed' : undefined,
      summary: devSquadCfg?.status === 'closed'
        ? 'Applications closed — trials November 2026'
        : 'Applications open — trials November 2026',
      // Tile disappears entirely until an admin switches the area on.
      hidden: devSquadCfg?.status !== 'live' && devSquadCfg?.status !== 'closed',
    },
    {
      title: 'Mudgeway Trophy',
      desc: 'The inter-club challenge trophy. Any affiliated club can challenge the current holder — the holder sets the date, venue and fish list, and has to be beaten on the day to give it up.',
      onClick: () => navigate('/mudgeway'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0V4z"/><path d="M17 5h2a2 2 0 0 1 0 4h-2"/><path d="M7 5H5a2 2 0 0 0 0 4h2"/><path d="m12 8.5.7 1.5 1.6.2-1.2 1.1.3 1.6-1.4-.8-1.4.8.3-1.6L9.7 10l1.6-.2z"/></svg>,
      status: 'live',
      summary: mudgewayHolder?.clubs?.name
        ? `Held by ${mudgewayHolder.clubs.name} since ${new Date(mudgewayHolder.held_from).toLocaleDateString('en-NZ', { month: 'short', year: 'numeric' })}`
        : 'Current holder, challenge queue and history',
    },
    {
      title: 'Big Fish',
      desc: 'Season-long biggest fish competition across six species — Butterfish, Blue Moki, Kingfish, Snapper, Warehou and Bluefin. Submit up to 3 fish per species with three verification photos. Live leaderboard per species.',
      onClick: () => navigate('/big-fish'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.47-3.44 6-7 6s-7.56-2.53-8.5-6Z"/><path d="M18 12v.5"/><path d="M16 17.93a9.77 9.77 0 0 1 0-11.86"/><path d="M7 10.67C7 8 5.58 5.97 2.73 5.5c-1 1.5-1 5 .5 8 1.5 3 3.5 3.5 3.77 3.5a8.23 8.23 0 0 1 0-6.33Z"/><path d="M10.46 7.26C10.2 5.88 9.17 4.24 8 3h5.8a2 2 0 0 1 1.98 1.67l.23 1.4"/><path d="m16.01 17.93-.23 1.4A2 2 0 0 1 13.8 21H9.5a5.96 5.96 0 0 1 1.49-3.98"/></svg>,
      status: 'sponsor',
      summary: 'Competition is live — log your catch now!',
    },
    {
      title: 'Fish Bingo',
      desc: 'Claim species you\'ve speared, earn points, and compete on the leaderboard. Complete bonus rows for extra points. Open to browse and register now — active SNZ members can claim.',
      onClick: () => navigate('/bingo'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><line x1="6.5" y1="6.5" x2="6.5" y2="6.51"/><line x1="17.5" y1="6.5" x2="17.5" y2="6.51"/><path d="m15.5 15.5 3 3m0-3-3 3"/></svg>,
      status: bingoWindow === 'after' ? 'wrapped' : 'live',
      summary: bingoWindow === 'open'
        ? '● Live now — claim your catches!'
        : bingoWindow === 'after'
          ? 'Season wrapped — check the leaderboard'
          : bingoCfg?.comp_start
            ? `Register now · claiming opens ${new Date(bingoCfg.comp_start).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}`
            : 'Register now',
    },
    {
      title: 'Other Competitions',
      desc: 'Club competitions — register your team, view fish lists, live leaderboard, and results. Want to run your own club comp? We can set it up for you.',
      onClick: () => navigate('/competitions'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>,
      status: 'live',
      summary: stats.compsTotal
        ? `${stats.compsTotal} total · ${stats.compsActive} active now`
        : 'Club competitions',
    },
    {
      title: 'Fish ID',
      desc: 'AI-assisted species identification. Take or upload a photo and get an instant ID with targeting tips, example catches, and MPI rules lookup.',
      onClick: () => navigate('/fish-id'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 12c.94-3.46 4.94-6 8.5-6 3.56 0 6.06 2.54 7 6-.94 3.47-3.44 6-7 6s-7.56-2.53-8.5-6Z"/><path d="M18 12v.5"/><path d="M16 17.93a9.77 9.77 0 0 1 0-11.86"/><path d="M7 10.67C7 8 5.58 5.97 2.73 5.5c-1 1.5-1 5 .5 8 1.5 3 3.5 3.5 3.77 3.5a8.23 8.23 0 0 1 0-6.33Z"/><path d="M10.46 7.26C10.2 5.88 9.17 4.24 8 3h5.8a2 2 0 0 1 1.98 1.67l.23 1.4"/><path d="m16.01 17.93-.23 1.4A2 2 0 0 1 13.8 21H9.5a5.96 5.96 0 0 1 1.49-3.98"/></svg>,
      status: 'live',
      summary: 'New AI-powered concept · Free for members to test',
      hidden: true,
    },
    {
      title: 'Inspiration',
      desc: 'Community recipes and cooking ideas from SNZ members. Search by species, share your own dishes, and discover new ways to cook your catch.',
      onClick: () => navigate('/inspiration'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 11h.01M11 15h.01M16 16H8a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v5a4 4 0 0 1-4 4Z"/><path d="M9 9h.01"/><path d="M8 20h8"/><path d="M12 16v4"/></svg>,
      status: 'live',
      summary: stats.recipes ? `${stats.recipes} recipes available now` : null,
    },
    {
      title: 'AGM & SGM',
      desc: 'Annual and Special General Meetings on the Hub — motions, attendance, voting and minutes. Active SNZ members only.',
      onClick: () => navigate('/agm'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V8l7-4 7 4v13"/><path d="M9 21v-6h6v6"/><path d="M9 11h.01"/><path d="M15 11h.01"/></svg>,
      status: agmMeetings.some(m => m.status === 'open') ? 'live' : 'soon',
      statusLabel: agmMeetings.length === 0 ? 'AGM 21st Jan 2027' : undefined,
      summary: (() => {
        const live = agmMeetings.find(m => m.status === 'open')
        if (live) return `● ${live.kind} live now — voting open`
        const next = agmMeetings[0]
        if (next) return `Next: ${next.kind} · ${new Date(next.meeting_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })}`
        return 'No meetings currently booked'
      })(),
    },
    {
      title: 'Vessel Near-Miss Survey',
      desc: 'Our national survey of vessel near-misses is now closed. Thanks to everyone who reported one — we are collating the insights and will share them with members and with the agencies who can act on them shortly.',
      onClick: () => navigate('/near-miss'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
      status: 'wrapped',
      summary: 'Survey closed · Insights coming soon',
    },
    {
      title: 'World Freshwater Champs',
      desc: 'WFSC 2026 — Lake Taupō, New Zealand · 13–14 March 2026. Live leaderboard, weigh-in results, teams, awards and full event information.',
      onClick: () => navigate('/wfsc'),
      icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={SNZ_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
      status: 'wrapped',
      summary: 'Results live · Lake Taupō · March 2026',
    },
  ]


  return (
    <div className="min-h-screen bg-white flex flex-col">
{/* NZUA membership banner */}
      <div className="bg-gray-900 px-6 py-2 flex items-center justify-center gap-2.5">
        <span className="text-xs font-semibold text-gray-400 tracking-wide whitespace-nowrap">Proud member of</span>
        <img src="/nzua-logo.svg" alt="New Zealand Underwater Association" className="h-7 w-auto object-contain" />
      </div>

      {/* Header with logo */}
      <header className="border-b border-gray-200 px-6 py-5 flex items-center justify-between bg-white">
        <div className="flex items-center gap-4">
          {SNZ_LOGO
            ? <img src={SNZ_LOGO} alt="Spearfishing NZ" className="h-16 w-auto object-contain" />
            : <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                {/* Mask lens frame */}
                <rect x="8" y="18" width="40" height="22" rx="11" stroke="#111" strokeWidth="2.5" fill="white"/>
                {/* Left lens */}
                <rect x="11" y="21" width="15" height="16" rx="6" fill={SNZ_BLUE} opacity="0.15" stroke={SNZ_BLUE} strokeWidth="1.5"/>
                {/* Right lens */}
                <rect x="30" y="21" width="15" height="16" rx="6" fill={SNZ_BLUE} opacity="0.15" stroke={SNZ_BLUE} strokeWidth="1.5"/>
                {/* Nose bridge */}
                <path d="M26 29 L30 29" stroke="#111" strokeWidth="2" strokeLinecap="round"/>
                {/* Strap left */}
                <path d="M8 24 Q4 22 4 18 Q4 14 8 14" stroke="#111" strokeWidth="2" fill="none" strokeLinecap="round"/>
                {/* Strap right */}
                <path d="M48 24 Q52 22 52 18 Q52 14 48 14" stroke="#111" strokeWidth="2" fill="none" strokeLinecap="round"/>
                {/* Snorkel */}
                <path d="M48 26 Q56 26 56 18 L56 10" stroke="#111" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                <circle cx="56" cy="10" r="2.5" fill={SNZ_BLUE}/>
              </svg>
          }
          <div>
            <div className="text-2xl font-black tracking-wide text-gray-900 leading-none">Spearfishing NZ</div>
          </div>
        </div>
        <HubMemberBadge />
      </header>

      {/* Hero */}
      <section className="px-6 py-14 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-black text-gray-900 leading-tight mb-4">
            Your national<br/>
            <span style={{ color: SNZ_BLUE }}>spearfishing hub</span>
          </h1>
          <p className="text-gray-500 max-w-md leading-relaxed">
            Membership, records, competition management, and event results — all in one place for New Zealand's spearfishing community.
          </p>
        </div>
      </section>

      {/* Carousel */}
      <HubCarousel />

      {/* Modules */}
      <section className="px-6 py-10 flex-1">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xs font-bold tracking-widest text-gray-400 uppercase mb-6">What would you like to do?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {modules.filter(m => !m.hidden).map((m) => {
              const st = m.status ? STATUS[m.status] : null
              return (
                <button
                  key={m.title}
                  onClick={m.onClick}
                  className="relative text-left p-6 rounded-2xl border-2 flex flex-col gap-3 transition-all group border-gray-200 bg-white hover:shadow-md cursor-pointer"
                  onMouseEnter={e => { e.currentTarget.style.borderColor = SNZ_BLUE }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb' }}
                >
                  {/* Status badge */}
                  {st && (
                    <span className="absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full border whitespace-nowrap"
                      style={{ background: st.bg, color: st.color, borderColor: st.border }}>
                      {m.statusLabel || st.label}
                    </span>
                  )}
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-50 border border-blue-100 flex-shrink-0">
                    {m.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-xl font-black text-gray-900 mb-1 pr-28">{m.title}</div>
                    <p className="text-sm text-gray-500 leading-relaxed">{m.desc}</p>
                  </div>
                  {m.summary && (
                    <div className="text-xs font-semibold rounded-lg px-3 py-1.5 border"
                      style={st ? { background: st.bg, color: st.color, borderColor: st.border } : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                      {m.summary}
                    </div>
                  )}
                  <div className="flex items-center gap-1 font-bold text-sm" style={{ color: SNZ_BLUE }}>
                    Open <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-5 bg-gray-50">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-between items-center gap-2">
          <span className="text-xs text-gray-400">© {new Date().getFullYear()} Spearfishing New Zealand Inc · Incorporated Society</span>
          <div className="flex gap-4 text-xs text-gray-400">
<a href="mailto:spearfishingnewzealand@gmail.com" className="hover:text-gray-600 transition">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

function AnalyticsTracker() {
  const { member } = useMemberSession()
  useAnalytics(member?.id || null)
  return null
}

export default function App() {
  return (
    <>
    <Routes>
      <Route path="/"              element={<SNZHub />} />
      <Route path="/records"       element={<NZRecordsPage />} />
      <Route path="/apply"         element={<RecordApplication />} />
      <Route path="/records/admin"   element={<ProtectedRoute><RecordsAdmin /></ProtectedRoute>} />
      <Route path="/records/admin/archive/:id" element={<ProtectedRoute><ApplicationArchive /></ProtectedRoute>} />
      <Route path="/fish-id"         element={<FishIDPage />} />
      <Route path="/fish-id/admin"   element={<ProtectedRoute><FishSpeciesAdmin /></ProtectedRoute>} />
      <Route path="/awards"        element={<AwardsPage />} />
      <Route path="/news"          element={<NewsPage />} />
      <Route path="/leaderboard"   element={<PublicLeaderboard />} />
      <Route path="/catfish"        element={<CatfishCullPage />} />
      <Route path="/catfish/register" element={<CatfishCullRegister />} />
      <Route path="/catfish/confirm"  element={<CatfishConfirm />} />
      <Route path="/dev-squad"         element={<DevSquadPage />} />
      <Route path="/admin/dev-squad"   element={<ProtectedRoute><DevSquadAdmin /></ProtectedRoute>} />
      <Route path="/near-miss"         element={<NearMissReport />} />
      <Route path="/near-miss/thanks"  element={<NearMissThanks />} />
      <Route path="/near-miss/results" element={<NearMissResults />} />
      <Route path="/admin/near-miss"   element={<ProtectedRoute><NearMissAdmin /></ProtectedRoute>} />
      <Route path="/membership/admin"       element={<MembershipAdmin />} />
      <Route path="/membership/*"           element={<MembershipRouter />} />
      <Route path="/membership/invited"       element={<MembershipRouter />} />
      <Route path="/nationals"              element={<NationalsPage />} />
      <Route path="/mudgeway"               element={<MudgewayPage />} />
      <Route path="/mudgeway/challenge"      element={<MudgewayChallenge />} />
      <Route path="/mudgeway/challenge/:id/respond" element={<MudgewayRespond />} />
      <Route path="/admin/mudgeway"          element={<MudgewayAdmin />} />
      <Route path="/nationals/admin"         element={<ProtectedNationalsRoute><NationalsAdmin /></ProtectedNationalsRoute>} />
      <Route path="/nationals/register"      element={<NationalsRegister />} />
      <Route path="/nationals/register/individual" element={<NationalsRegisterIndividual />} />
      <Route path="/nationals/confirm"       element={<NationalsConfirm />} />
      <Route path="/nationals/buddy-finder"  element={<NationalsBuddyFinder />} />
      <Route path="/nationals/leaderboard"   element={<NationalsLeaderboard />} />
      <Route path="/competitions"           element={<CompetitionsPage />} />
      <Route path="/competitions/admin"     element={<CompSuperAdmin />} />
      <Route path="/competitions/:id"       element={<CompetitionDetail />} />
      <Route path="/competitions/:id/register" element={<CompRegister />} />
      <Route path="/competitions/:id/admin" element={<CompAdmin />} />
      <Route path="/c/:id" element={<CompDeepLink />} />
      <Route path="/checkin"       element={<CheckInDisplay />} />
      <Route path="/admin/login"   element={<AdminLogin />} />
      <Route path="/admin"         element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/admin/teams"   element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
      <Route path="/admin/weighin" element={<ProtectedRoute><WeighmasterInterface /></ProtectedRoute>} />
      <Route path="/admin/results"   element={<ProtectedRoute><ResultsManagement /></ProtectedRoute>} />
      <Route path="/admin/carousel"  element={<ProtectedRoute><CarouselAdmin /></ProtectedRoute>} />
      <Route path="/admin/big-fish"  element={<ProtectedRoute><BigFishAdmin /></ProtectedRoute>} />
      <Route path="/big-fish"          element={<BigFishPage />} />
      <Route path="/big-fish/sponsor" element={<BigFishSponsorPage />} />
      {/* Inspiration / Community Recipes */}
      <Route path="/inspiration"          element={<InspirationPage />} />
      {/* Fish Bingo */}
      <Route path="/bingo"                element={<BingoApp />} />
      <Route path="/bingo/diver"          element={<BingoDiverPage />} />
      <Route path="/bingo/admin"          element={<ProtectedBingoRoute><BingoAdmin /></ProtectedBingoRoute>} />
      <Route path="/bingo/admin/species"  element={<ProtectedBingoRoute><BingoSpeciesAdmin /></ProtectedBingoRoute>} />
      <Route path="/bingo/admin/bonuses"  element={<ProtectedBingoRoute><BingoBonusAdmin /></ProtectedBingoRoute>} />
      <Route path="/bingo/admin/setup"    element={<ProtectedBingoRoute><BingoConfigAdmin /></ProtectedBingoRoute>} />
      <Route path="/bingo/admin/photos"   element={<ProtectedBingoRoute><BingoPhotoExportAdmin /></ProtectedBingoRoute>} />
      <Route path="/bingo/admin/registrations" element={<ProtectedBingoRoute><BingoRegistrationsAdmin /></ProtectedBingoRoute>} />
      {/* AGM / SGM */}
      <Route path="/agm"            element={<AGMPage />} />
      <Route path="/agm/admin"      element={<ProtectedRoute><AGMAdmin /></ProtectedRoute>} />
      <Route path="/agm/:id"        element={<AGMPage />} />
      {/* World Freshwater Spearfishing Championships */}
      <Route path="/wfsc/*"          element={<WFSCApp />} />
      <Route path="*"              element={<Navigate to="/" replace />} />
    </Routes>
    <AnalyticsTracker />
    <PWAInstallPrompt />
    </>
  )
}
